import { NavLink } from 'react-router';
import type { Role, ViewKey } from '@/domain';
import { SIDEBAR_HEADING, strings } from '@/shared/strings';
import { CloseIcon, KeyIcon, ScanIcon, SignOutIcon } from '../icons';
import { AppLogo } from './AppLogo';
import { navItemsFor } from './navItems';
import { useShellStore } from './shellStore';
import styles from './Sidebar.module.css';
import { initials } from '@/shared/format/initials';

export interface SidebarProps {
  role: Role;
  /** Resolved by the server for this user. The menu is this list, in this order. */
  views: readonly ViewKey[];
  /** Whether this session may open the scanner, resolved from the session views. */
  canScan: boolean;
  userName: string;
  userTitle: string;
  /** Opens the change password dialog. On every screen, for every role. */
  onChangePassword: () => void;
  onSignOut: () => void;
  avatarSrc?: string | null;
  version?: string;
  /** Your profile, from your name and photograph. Request of 4 October 2026 (second), section 7. */
  onOpenProfile?: () => void;
}

/**
 * The menu, derived from `session.views`. Section 5.1.
 *
 * There is no hard coded list of screens per role here and there must not be
 * one: the access matrix is editable at runtime, and a role that gains a screen
 * gains a menu entry on the next render.
 */
export function Sidebar({
  role,
  views,
  canScan,
  userName,
  userTitle,
  onChangePassword,
  onSignOut,
  avatarSrc = null,
  version,
  onOpenProfile,
}: SidebarProps) {
  const navOpen = useShellStore((state) => state.navOpen);
  const closeNav = useShellStore((state) => state.closeNav);
  const items = navItemsFor(views);

  /*
   * The scanner belongs in the menu, not only on the phone.
   *
   * `scan` is a sub-view and carries no grant of its own, so it never appears
   * in `views` and the menu never showed it. That was fine while the bench was
   * assumed to be a handset, and wrong the moment a technician sat at a desktop
   * station: the tab bar is hidden above 900px, so there was no way to reach
   * the scanner at all. It matters more now that scanning is the ONLY way to
   * take a case.
   *
   * Shown to everyone who holds `scan`, Management included. The client asked
   * for it: a manager scans a tray to look at the case, not to work it, and the
   * job view knows the difference. First in the list for a bench, where it is
   * the action taken most; last for a manager, whose menu is ten screens long.
   */
  const worksABench = role === 'tech' || role === 'cadcam';

  return (
    <aside
      className={`${styles.sb} ${navOpen ? styles.open : ''}`}
      id="sidebar"
      aria-label={strings.shell.menu}
    >
      <div className={styles.brand}>
        <div className={styles.mark}>
          <AppLogo />
        </div>
        <div className={styles.name}>
          <b>{strings.app.name}</b>
          <span>{strings.app.tagline}</span>
        </div>
      </div>

      <button className={styles.close} onClick={closeNav} aria-label={strings.shell.closeMenu}>
        <CloseIcon />
      </button>

      <nav>
        <div className={styles.heading}>{SIDEBAR_HEADING[role]}</div>

        {canScan && worksABench && <ScanLink onNavigate={closeNav} />}

        {items.map((item) => (
          <NavLink
            key={item.view}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) => `${styles.item} ${isActive ? styles.on : ''}`}
            onClick={closeNav}
          >
            <span className={styles.ic}>{item.icon}</span>
            {item.label}
          </NavLink>
        ))}

        {canScan && !worksABench && <ScanLink onNavigate={closeNav} />}
      </nav>

      <div className={styles.user}>
        <button
          type="button"
          className={styles.me}
          aria-label={strings.profile.open}
          onClick={() => {
            closeNav();
            onOpenProfile?.();
          }}
        >
          <span className={styles.av} aria-hidden="true">
            {avatarSrc === null ? initials(userName) : <img src={avatarSrc} alt="" />}
          </span>
          <span className={styles.who}>
            <b>{userName}</b>
            <span>{userTitle}</span>
          </span>
        </button>
        {/*
          Two buttons on the user block, on every screen for every role, which is
          what "accessible across the system" means: a key for changing your own
          password, then sign out. Labelled for a screen reader; the icons say
          nothing on their own.
        */}
        <div className={styles.tools}>
          <button
            className={styles.tool}
            onClick={onChangePassword}
            aria-label={strings.password.action}
            title={strings.password.action}
          >
            <KeyIcon />
          </button>
          <button
            className={styles.tool}
            onClick={onSignOut}
            aria-label={strings.shell.signOut}
            title={strings.shell.signOut}
          >
            <SignOutIcon />
          </button>
        </div>
      </div>
      {version !== undefined && <p className={styles.version}>{version}</p>}
    </aside>
  );
}

/** Its own component so the two placements cannot drift apart. */
function ScanLink({ onNavigate }: { onNavigate: () => void }) {
  return (
    <NavLink
      to="/scan"
      className={({ isActive }) => `${styles.item} ${styles.scan} ${isActive ? styles.on : ''}`}
      onClick={onNavigate}
    >
      <span className={styles.ic}>
        <ScanIcon />
      </span>
      {strings.shell.scan}
    </NavLink>
  );
}
